import { createPortal } from "react-dom";
import { useSelector, useDispatch } from "react-redux";
import { useMemo, useState } from "react";
import { useEffect } from "react";
import { useRef } from "react";
import { useAuth } from "react-oidc-context";
import { $authHost } from "../../http";
import {
    Box,
    Typography,
    TextField,
    IconButton,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Paper,
    Fab,
    InputAdornment,
    ThemeProvider,
    createTheme,
    Button,
    Snackbar,
    Alert,
    TableContainer,
    Chip,
    CircularProgress,
    Select,
    MenuItem,
    FormControl
} from "@mui/material";

import {
    Assignment as AssignmentIcon,
    LightMode as LightModeIcon,
    DarkMode as DarkModeIcon,
    Close as CloseIcon,
    Edit as EditIcon,
    ChecklistRtl as ChecklistRtlIcon,
    Clear as ClearIcon,
    Search as SearchIcon,
    ArrowUpward as ArrowUpwardIcon,
    Print as PrintIcon,
    Warning as WarningIcon,
    Save as SaveIcon,
    Cancel as CancelIcon,
    RemoveCircle as RemoveCircleIcon,
    Block as BlockIcon,
    TaskAlt as TaskAltIcon,
    CheckCircle as CheckCircleIcon,
    List as ListIcon,
    Event as EventIcon,
    Engineering as EngineeringIcon
} from '@mui/icons-material';
import { setModal } from "../../store/reducers/helpersReducer";
import { fetchRules, toggleDarkMode } from "../../store/reducers/stateReducer";
import { selectAccountingsFiltered, selectAccountingsGroupedByTowns } from "../../store/selectors/supervisionSelector";
import html2canvas from "html2canvas";
import { getPaperDimensions } from "../controls/PrintControl";
import jsPDF from "jspdf";

const textLength = {
    1: [300, 330],
    2: [300, 330],
    3: [280, 440],
    4: [220, 480]
}

const numberFormat = new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
})

const EditableCell = ({ value, onChange, darkMode, disabled }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value ?? '');

    useEffect(() => {
        setCurrentValue(value ?? '');
    }, [value]);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            setIsEditing(false);
            onChange(currentValue);
        } else if (e.key === 'Escape') {
            setIsEditing(false);
            setCurrentValue(value ?? ''); // Revert to original or empty string
        }
    };

    const handleBlur = () => {
        setIsEditing(false);
        onChange(currentValue);
    };

    const handleInputChange = (e) => {
        const val = e.target.value;
        if (val === '' || /^-?\d*([.,])?\d*$/.test(val)) {
            setCurrentValue(val);
        }
    };

    if (isEditing) {
        return (
            <TextField
                value={currentValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                autoFocus
                size="small"
                sx={{
                    width: "100%",

                    '& .MuiOutlinedInput-root': {
                        backgroundColor: darkMode ? '#1e1e1e' : 'white',
                        color: darkMode ? 'white' : 'black',
                    },
                    '& .MuiInputBase-input': {
                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                        color: darkMode ? 'white' : 'black',
                        padding: '4px 8px',
                        textAlign: 'center'
                    },
                }}
            />
        );
    }

    return (
        <Box
            onClick={() => !disabled ? setIsEditing(true) : null}
            sx={{
                cursor: 'pointer',
                width: '100%',
                // minHeight: '24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Typography sx={{
                fontSize: { xs: '0.7rem', md: '0.8rem' }
            }}>
                {numberFormat.format(currentValue || 0)}
            </Typography>
        </Box>
    );
};

const SelectableCell = ({ value, onChange, darkMode, disabled, options = [] }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [currentValue, setCurrentValue] = useState(value ?? '');

    useEffect(() => {
        setCurrentValue(value ?? '');
    }, [value]);

    const handleChange = (event) => {
        const newValue = event.target.value;
        setCurrentValue(newValue);
        onChange(newValue);
        setIsEditing(false);
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Escape') {
            setIsEditing(false);
            setCurrentValue(value ?? '');
        }
    };

    if (isEditing) {
        return (
            <FormControl size="small" sx={{ width: "100%" }}>
                <Select
                    value={currentValue}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    autoFocus
                    sx={{
                        '& .MuiOutlinedInput-root': {
                            backgroundColor: darkMode ? '#1e1e1e' : 'white',
                            color: darkMode ? 'white' : 'black',
                        },
                        '& .MuiSelect-select': {
                            fontSize: { xs: '0.7rem', md: '0.8rem' },
                            color: darkMode ? 'white' : 'black',
                            padding: '4px 8px',
                            textAlign: 'center'
                        },
                    }}
                    MenuProps={{
                        sx: {
                            zIndex: 9999,
                        },
                        PaperProps: {
                            sx: {
                                '& .MuiMenuItem-root': {
                                    // color: 'black',
                                    minHeight: '10px',
                                    fontSize: { xs: '0.65rem', md: '0.8rem' },
                                }
                            }
                        }
                    }}
                >
                    {options.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        );
    }

    return (
        <Box
            onClick={() => !disabled ? setIsEditing(true) : null}
            sx={{
                cursor: disabled ? 'default' : 'pointer',
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}
        >
            <Typography sx={{
                fontSize: { xs: '0.7rem', md: '0.8rem' }
            }}>
                {currentValue || '-'}
            </Typography>
        </Box>
    );
};

const colors = { 'П': [147, 51, 234], 'Г': [16, 185, 129], 'С': [59, 130, 246], 'Р': [246, 34, 58], 'Ш': [255, 127, 39] };

const ModalRules = () => {
    const darkMode = useSelector(state => state.state.darkMode)
    const dispatch = useDispatch()
    const [textSearch, setTextSearch] = useState('')
    const containerRef = useRef(null)
    const rules = useSelector(state => state.state.rules)
    const user = useSelector(state => state.state.user)
    const [sortBy, setSortBy] = useState('code');
    const [sortOrder, setSortOrder] = useState('asc');
    const [editedCosts, setEditedCosts] = useState({})
    const [editedComplexity, setEditedComplexity] = useState({})
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [localPrinting, setLocalPrinting] = useState(false)
    const [showEmptyRows, setShowEmptyRows] = useState(true);
    const accountingTownsFiltered = useSelector(selectAccountingsGroupedByTowns)
    // const filter = useSelector(state => state.state.filter)
    const accountingTowns = useSelector(selectAccountingsFiltered)
    
    // Опции для поля сложности
    const complexityOptions = [
        { value: 'Сложное', label: 'Сложное' },
        { value: 'Не сложное', label: 'Не сложное' }
    ];
    
    // выбранные теги
   
    const tags = useMemo(() => {
        return ["Все", ...new Set(accountingTowns.flatMap(acc => acc.checklist).flat().filter(f => f))]
    }, [accountingTowns])
    const data = useMemo(() => {
        return ["Все", ...accountingTownsFiltered.flatMap(acc => acc.tps.flatMap(tp => tp.pillars.flatMap(pillar => pillar?.accounting?.checklist?.filter(ch => !pillar?.accounting?.checklist_elim?.success?.includes(ch)))))]
    }, [accountingTownsFiltered])

    // количество записей по тегу
    // const counts = useMemo(() => Object.fromEntries(tags.map((t) => [t, data.filter((d) => d === t).length])), [tags, data]);
    const counts = useMemo(() => {
        const tagCounts = Object.fromEntries(tags.map((t) => [t, data.filter((d) => d === t).length]));
        // Для тега "Все" берем максимальное значение из других тегов
        if (tags.includes("Все")) {
            const maxCount = Math.max(...Object.entries(tagCounts)
                .filter(([tag]) => tag !== "Все")
                .map(([_, count]) => count));
            tagCounts["Все"] = maxCount;
        }
        return tagCounts;
    }, [tags, data]);
    console.log(counts)

    const hiddenCodes = useMemo(() => {
        if (!rules) return [];
        return rules
            .flatMap(group => group.rules)
            .map(rule => rule.code)
            .filter(code => !counts[code] || counts[code] === 0);
    }, [rules, counts]);


    console.log(rules)

    const SortIndicator = ({ column }) => {
        if (sortBy !== column) return null;
        return sortOrder === 'asc' ? ' ▲' : ' ▼';
    };

    const handleSort = (column) => {
        if (sortBy === column) {
            setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
        } else {
            setSortBy(column);
            setSortOrder('asc');
        }
    };

    // Create MUI theme based on darkMode mode
    const muiTheme = createTheme({
        palette: {
            mode: darkMode ? 'dark' : 'light',
            background: {
                default: darkMode ? '#121212' : '#ffffff',
                paper: darkMode ? '#1e1e1e' : '#ffffff'
            },
            text: {
                primary: darkMode ? '#e0e0e0' : '#000000'
            }
        }
    });

    const getSortedRules = () => {
        if (!rules) return [];

        const searchWords = textSearch.toLowerCase().split(/\s+/).filter(word => word.length > 0);
        console.log(searchWords)
        const filtered = rules.map(group => (
            {
                ...group,
                rules: group.rules.filter(rule =>
                    searchWords.every(word =>
                        rule.code.toLowerCase().includes(word) ||
                        rule.name.toLowerCase().includes(word) ||
                        rule.description.toLowerCase().includes(word) ||
                        rule.foreshortening.toLowerCase().includes(word)
                    )
                ).sort((a, b) => {
                    if (sortBy === 'code') {
                        return sortOrder === 'asc' ?
                            parseFloat(a.code.match(/\d+(\.\d+)?/g)?.[0] || -1) - parseFloat(b.code.match(/\d+(\.\d+)?/g)?.[0] || -1) :
                            parseFloat(b.code.match(/\d+(\.\d+)?/g)?.[0] || -1) - parseFloat(a.code.match(/\d+(\.\d+)?/g)?.[0] || -1)
                    }
                })
            }
        )).filter(group => group.rules.length > 0)

        console.log(filtered)
        return filtered
    };

    const toggleButtonStyle = {
        border: 'none',
        background: 'transparent',
        color: 'inherit',
        fontSize: '1.5em',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        transition: 'color 0.3s ease, transform 0.3s ease',
        zIndex: 9999,
        '&:hover': {
            transform: 'scale(1.2)',
        }
    };

    const handleCostChange = (rule, newCost) => {
        const { id, cost: originalCost } = rule;

        const processedNewCost = newCost.trim() === '' ? null : parseFloat(newCost.replace(',', '.'));

        if (newCost.trim() !== '' && isNaN(processedNewCost)) {
            setSnackbar({ open: true, message: 'Стоимость должна быть числом', severity: 'warning' });
            return;
        }

        const originalNumericCost = originalCost === null ? null : parseFloat(originalCost);

        if (processedNewCost === originalNumericCost) {
            setEditedCosts(prev => {
                const newEditedCosts = { ...prev };
                delete newEditedCosts[id];
                return newEditedCosts;
            });
        } else {
            setEditedCosts(prev => ({
                ...prev,
                [id]: processedNewCost
            }));
        }
    };

    const handleComplexityChange = (rule, newComplexity) => {
        const { id, complexity: originalComplexity } = rule;

        if (newComplexity === originalComplexity) {
            setEditedComplexity(prev => {
                const newEditedComplexity = { ...prev };
                delete newEditedComplexity[id];
                return newEditedComplexity;
            });
        } else {
            setEditedComplexity(prev => ({
                ...prev,
                [id]: newComplexity
            }));
        }
    };

    const handleCancelChanges = () => {
        setEditedCosts({});
        setEditedComplexity({});
        setSnackbar({ open: true, message: 'Изменения отменены', severity: 'warning' });
    };

    const handleSaveChanges = async () => {
        const totalChanges = Object.keys(editedCosts).length + Object.keys(editedComplexity).length;
        
        if (totalChanges === 0) {
            setSnackbar({ open: true, message: 'Нет изменений для сохранения', severity: 'info' });
            return;
        }

        setSnackbar({ open: true, message: 'Сохранение изменений...', severity: 'info' });

        try {
            const changes = [
                ...Object.entries(editedCosts).map(([id, cost]) => ({
                    id: parseInt(id),
                    field: 'cost',
                    value: parseFloat(cost) || 0
                })),
                ...Object.entries(editedComplexity).map(([id, complexity]) => ({
                    id: parseInt(id),
                    field: 'complexity',
                    value: complexity
                }))
            ];

            const response = await $authHost.post('/apig/state/rules', {
                user_id: user.vio_id,
                changes: changes
            });

            if (response.status >= 200 && response.status < 300) {
                dispatch(fetchRules())
                setEditedCosts({});
                setEditedComplexity({});
                setSnackbar({ open: true, message: 'Изменения успешно сохранены!', severity: 'success' });
            } else {
                throw new Error(response.data?.message || 'Ошибка сохранения');
            }
        } catch (error) {
            console.error('Ошибка при сохранении изменений:', error);
            setSnackbar({ open: true, message: error.message || 'Произошла ошибка при сохранении', severity: 'error' });
        }
    };

    async function printPDF() {
        const a4 = getPaperDimensions('A4', 'landscape');
        const MM_TO_INCH = 1 / 25.4;
        const dpi = 120;
        var w = ((a4.width - 10) * MM_TO_INCH) * dpi;
        var h = ((a4.height - 20) * MM_TO_INCH) * dpi;
        const delay = ms => new Promise(
            resolve => setTimeout(resolve, ms)
        );
        let tableImages = [];
        const oldWidth = document.getElementById('rules_modal').style.width;
        document.getElementById('rules_modal').style.width = `${w}px`;
        await delay(1000);
        const tables = document.getElementsByName('rules-table');
        for (let i = 0; i < tables.length; i++) {
            const table = tables[i];
            const headerHeight = table.childNodes[0].getBoundingClientRect().height;
            const bodyHeight = table.childNodes[1].getBoundingClientRect().height;

            const rows = [...table.childNodes[1].childNodes];
            let rowsGroupsHeight = [];
            let height = 0.0;
            rows.map((r, index) => {
                const rowH = r.getBoundingClientRect().height;
                if ((height + rowH) <= (h - headerHeight)) {
                    height += rowH;

                } else {
                    rowsGroupsHeight.push(height);
                    height = rowH;
                }
            });
            rowsGroupsHeight.push(height);
            console.log(h, rowsGroupsHeight)

            const headerImgData = await html2canvas(table.childNodes[0], {
                useCORS: true,
                logging: true,
                width: w,
                height: headerHeight,
                scale: 1
            });

            const bodyImgData = await html2canvas(table.childNodes[1], {
                useCORS: true,
                logging: true,
                width: w,
                height: rowsGroupsHeight.reduce((a, v) => a + v, 0),
                scale: 1,
                allowTaint: true,
                foreignObjectRendering: false,
                removeContainer: true,
                backgroundColor: '#ffffff'
            });

            const parts = [];
            for (let y = 0; y < rowsGroupsHeight.length; y++) {
                const temph = rowsGroupsHeight[y];
                const tempw = bodyImgData.width;
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = tempw;
                tempCanvas.height = temph;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.drawImage(bodyImgData, 0, rowsGroupsHeight.slice(0, y).reduce((t, r) => t + r, 0), tempw, temph, 0, 0, tempw, temph);
                parts.push({
                    width: (tempCanvas.width / dpi) / MM_TO_INCH,
                    height: (tempCanvas.height / dpi) / MM_TO_INCH,
                    url: tempCanvas.toDataURL("image/png", 1.0)
                });
            }
            tableImages.push({
                header: {
                    width: (headerImgData.width / dpi) / MM_TO_INCH,
                    height: (headerImgData.height / dpi) / MM_TO_INCH,
                    url: headerImgData.toDataURL("image/png", 1.0)
                },
                body: parts
            });
        }
        document.getElementById('rules_modal').style.width = oldWidth;

        const pdf = new jsPDF({ format: [a4.width, a4.height], orientation: 'landscape', compress: false });
        pdf.setFont("Roboto-Medium");
        pdf.setFontSize(11);
        pdf.setLanguage('ru');

        const xOffset = 5;
        const yOffset = 5;
        const textHeigth = 5;
        const pagesCount = tableImages.reduce((t, r) => t + r.body.length, 0);
        let currentPage = 1;
        pdf.setTextColor('#8A8889');
        pdf.setFontSize(10);
        const date = new Date();
        const sdate = `${("0" + date.getDate()).slice(-2)}.${("0" + (date.getMonth() + 1)).slice(-2)}.${date.getFullYear()}`;

        // Итерация по таблицам
        for (let i = 0; i < tableImages.length; i++) {
            // Итерация по страницам таблицы
            for (let y = 0; y < tableImages[i].body.length; y++) {
                if (currentPage !== 1) {
                    pdf.addPage([a4.width, a4.height], 'landscape');
                }
                // Колонтитул
                pdf.text(`Чек-лист контроля качества строительства от ${sdate}`, a4.width - xOffset, yOffset, { align: "right", baseline: "top" });

                // Заглавие таблицы
                pdf.addImage(tableImages[i].header.url, 'image/png', xOffset, yOffset + textHeigth, a4.width - (xOffset * 2), tableImages[i].header.height, undefined, "MEDIUM");
                // Тело таблицы
                pdf.addImage(tableImages[i].body[y].url, 'image/png',
                    xOffset,
                    yOffset + tableImages[i].header.height + textHeigth,
                    a4.width - (xOffset * 2),
                    tableImages[i].body[y].height,
                    undefined,
                    "MEDIUM");
                pdf.text(`стр. ${currentPage} из ${pagesCount}`, a4.width - xOffset, a4.height - yOffset, { align: "right", baseline: "bottom" });
                currentPage = currentPage + 1;
            }
        }
        pdf.save(`Чек-лист контроля качества строительства от ${sdate}.pdf`);
        setLocalPrinting(false);
    }

    useEffect(() => {
        if (localPrinting) {
            printPDF();
        }
    }, [localPrinting]);

    const scrollButtonStyle = {
        position: 'fixed',
        bottom: '30px',
        right: '40px',
        background: '#97ec37',
        color: 'white',
        fontSize: '1.5em',
        cursor: 'pointer',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 9999,
        transition: 'color 0.3s ease, transform 0.3s ease',
        borderRadius: '50%',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
        width: '40px',
        height: '40px',
        opacity: 0.5,
        '&:hover': {
            transform: 'scale(1.2)',
            opacity: 1,
        }
    };

    const tableHeaderStyle = {
        fontWeight: 'bold',
        textAlign: 'center',
        padding: '8px 4px',
        backgroundColor: darkMode ? '#388E3C' : '#66BB6A', // Brighter light green
        color: darkMode ? 'white' : 'black',
        fontSize: { xs: '0.7rem', md: '0.8rem' }
    };

    const tableCellStyle = {
        // padding: '8px 8px',
        // verticalAlign: 'middle',
        // lineHeight: 1,
        // fontSize: '14px',
        // borderBottom: '1px solid #ddd'

        textAlign: 'center',
        color: darkMode ? 'white' : 'black',
        padding: '6px 8px',
        verticalAlign: 'middle',
        lineHeight: 1,
        fontSize: { xs: '0.7rem', md: '0.8rem' }
    };

    const evenRowStyle = {
        backgroundColor: darkMode ? '#1B3D1D' : '#C8E6C9' // Brighter even row for light mode
    };

    // return null;

    return (
        createPortal(
            <ThemeProvider theme={muiTheme}>
                <Box
                    id="rules_modal"
                    sx={{
                        position: 'absolute',
                        display: 'flex',
                        flexDirection: 'column',
                        top: 0,
                        zIndex: 8000,
                        backgroundColor: 'background.default',
                        color: 'text.primary',
                        width: '100%',
                        height: '100%',
                        overflowY: 'auto',
                        transition: 'all 0.3s ease'
                    }}
                    ref={containerRef}
                >
                    <Box sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'center' },
                        gap: 1,
                        pt: { xs: 0.5, md: 1 },
                        p: { xs: 1, md: 2 },
                    }}>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                            <ChecklistRtlIcon sx={{ fontSize: { xs: 24, md: 32 } }} />
                            <Typography variant="h6" sx={{ fontWeight: 'bold', fontSize: { xs: '1rem', md: '1.2rem' }, lineHeight: 1.1 }}>
                                Чек-лист контроля качества строительства
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 1 }}>
                            <TextField
                                placeholder="Поиск"
                                variant="outlined"
                                size="small"
                                value={textSearch}
                                onChange={(e) => setTextSearch(e.target.value)}
                                sx={{
                                    width: 280,
                                    margin: '0 10px',
                                    '& .MuiOutlinedInput-root': {
                                        '& fieldset': {
                                            borderColor: '#ccc',
                                            borderRadius: '4px'
                                        }
                                    },
                                    '& .MuiInputBase-input': {
                                        fontSize: { xs: '0.7rem', md: '0.8rem' },
                                        color: darkMode ? 'white' : 'black',
                                    },
                                }}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <SearchIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: textSearch && (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setTextSearch('')}
                                                    size="small"
                                                    sx={{
                                                        fontSize: { xs: 18, md: 24 }
                                                    }}
                                                >
                                                    <CloseIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
                                                </IconButton>
                                            </InputAdornment>
                                        )
                                    }
                                }}

                            />
                            {/* <IconButton
                                onClick={() => dispatch(toggleDarkMode())}
                                sx={{
                                    color: 'text.primary',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: { xs: 18, md: 24 },
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'color 0.3s ease, transform 0.3s ease',
                                    zIndex: 9999,
                                    '&:hover': {
                                        transform: 'scale(1.2)'
                                    }
                                }}
                            >
                                {darkMode ? <LightModeIcon sx={{ fontSize: { xs: 18, md: 24 } }} /> : <DarkModeIcon sx={{ fontSize: { xs: 18, md: 24 } }} />}
                            </IconButton> */}
                            <IconButton
                                onClick={() => dispatch(setModal(null))}
                                sx={{
                                    color: 'text.primary',
                                    border: 'none',
                                    background: 'transparent',
                                    fontSize: { xs: 18, md: 24 },
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    transition: 'color 0.3s ease, transform 0.3s ease',
                                    zIndex: 9999,
                                    '&:hover': {
                                        transform: 'scale(1.2)'
                                    }
                                }}
                            >
                                <CloseIcon sx={{ fontSize: { xs: 18, md: 24 } }} />
                            </IconButton>
                        </Box>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, p: { xs: '0 10px 10px', md: '0 16px 16px' } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                            <Chip
                                icon={showEmptyRows ? <BlockIcon sx={{ fontSize: { xs: 18, md: 24 }}}/> : <ListIcon sx={{ fontSize: { xs: 18, md: 24 }}}/>}
                                label={showEmptyRows ? 'Скрыть пустые' : 'Отобразить все'}
                                onClick={() => setShowEmptyRows(!showEmptyRows)}
                                color={showEmptyRows ? "default" : "primary"}
                                sx={{
                                    fontWeight: 'bold',
                                    fontSize: { xs: '0.7rem', md: '0.8rem' },
                                    '&:hover': {
                                        opacity: 0.8
                                    }
                                }}
                                variant="filled"
                                clickable
                            />
                            {!showEmptyRows && hiddenCodes.length > 0 && (
                                <Chip
                                    label={
                                        <Box component="span" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
                                            <Typography component="span" sx={{ mr: 0.5, fontWeight: 'bold', fontSize: 'inherit' }}>
                                                Скрытые:
                                            </Typography>
                                            {hiddenCodes.map((code, index) => (
                                                <Typography component="span" key={code} sx={{
                                                    color: `rgba(${(colors[code[0]] || (darkMode ? [255, 255, 255] : [0, 0, 0])).join(',')},1)`,
                                                    fontWeight: 'bold',
                                                    fontSize: 'inherit'
                                                }}>
                                                    {code}{index < hiddenCodes.length - 1 ? ',\u00A0' : ''}
                                                </Typography>
                                            ))}
                                        </Box>
                                    }
                                    onClick={() => { }}
                                    variant="filled"
                                    sx={{
                                        height: 'auto',
                                        '& .MuiChip-label': {
                                            display: 'block',
                                            whiteSpace: 'normal',
                                            paddingTop: '3px',
                                            paddingBottom: '3px',
                                        },
                                        fontWeight: 'bold',
                                        fontSize: { xs: '0.7rem', md: '0.8rem' }
                                    }}
                                />
                            )}
                        </Box>
                        
                        <Chip
                            label={'Печать'}
                            icon={<PrintIcon color="black" sx={{ fontSize: { xs: 18, md: 24 } }} />}
                            onClick={() => setLocalPrinting(true)}
                            sx={{
                                backgroundColor: '#deb887',
                                color: 'black',
                                fontWeight: 'bold',
                                fontSize: { xs: '0.7rem', md: '0.8rem' },
                                '&:hover': {
                                    backgroundColor: '#deb887',
                                    opacity: 0.8
                                }
                            }}
                        />
                    </Box>

                    {/* Tables */}
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', p: '10px' }}>
                        {rules && getSortedRules(rules).map((group, i) => {
                            const filteredRules = group.rules.filter(rule => showEmptyRows || (counts[rule.code] && counts[rule.code] > 0));

                            if (filteredRules.length === 0) {
                                return null;
                            }
                            return (
                                <TableContainer
                                    key={i}
                                    component={Paper}
                                    sx={{
                                        overflowX: 'unset',
                                        width: '100%',
                                        '& .MuiTable-root': {
                                            borderCollapse: 'collapse',
                                            // width: '100%'
                                        },
                                        '& .MuiPaper-root': {
                                            overflowX: 'unset'
                                        }
                                    }}

                                >
                                    <Table stickyHeader aria-label="sticky table" name="rules-table" id="rules-table">
                                        <TableHead sx={{ zIndex: 1000 }}>
                                            <TableRow>
                                                <TableCell
                                                    colSpan={9}
                                                    sx={{
                                                        backgroundColor: 'background.paper',
                                                        color: 'text.primary',
                                                        p: 0,
                                                        border: 'none'
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'left',
                                                            borderRadius: 1,
                                                            borderLeft: darkMode ? '8px solid #388E3C' : '8px solid #66BB6A',
                                                            p: 1,
                                                            mb: 1,
                                                            backgroundColor: 'background.default'
                                                            // display: 'flex',
                                                            // justifyContent: 'left',
                                                            // borderTopLeftRadius: '6px',
                                                            // borderLeft: "8px solid #80cd5c",
                                                            // padding: '3px 10px',
                                                            // marginBottom: '0px',
                                                            // fontSize: { xs: '18px', md: '20px' }
                                                        }}
                                                    >
                                                        <Typography variant="h6"
                                                            sx={{
                                                                fontSize: { xs: '1rem', md: '1.2rem' },
                                                                fontWeight: 'bold'
                                                            }}>
                                                            {group.group}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                            <TableRow>
                                                <TableCell
                                                    onClick={() => handleSort('code')}
                                                    sx={{
                                                        ...tableHeaderStyle,
                                                        whiteSpace: 'nowrap',
                                                        cursor: 'pointer',
                                                        '&:hover': {
                                                            opacity: 0.8
                                                        }
                                                    }}
                                                >
                                                    Код <SortIndicator column="code" />
                                                </TableCell>
                                                <TableCell sx={tableHeaderStyle}>Примеры</TableCell>
                                                <TableCell sx={tableHeaderStyle}>Количество</TableCell>
                                                <TableCell sx={tableHeaderStyle}>
                                                    Сложность {<EditIcon sx={{ fontSize: '16px' }} />}
                                                </TableCell>
                                                <TableCell sx={tableHeaderStyle}>Приоритет</TableCell>
                                                <TableCell sx={tableHeaderStyle}>Отклонение</TableCell>
                                                <TableCell sx={tableHeaderStyle}>Предписание</TableCell>
                                                <TableCell sx={tableHeaderStyle}>Фото инструкция</TableCell>
                                                <TableCell sx={tableHeaderStyle}>
                                                    Стоимость {<EditIcon sx={{ fontSize: '16px' }} />}</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {filteredRules.sort((a, b) => {
                                                return sortOrder === 'asc' ? a.num_code - b.num_code : b.num_code - a.num_code
                                            }).map((rule, indx) => (
                                                <TableRow
                                                    key={indx}
                                                    sx={indx % 2 === 1 ? evenRowStyle : {}}
                                                >
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle,
                                                            color: `rgba(${(colors[rule.code[0]] || [255, 255, 255]).join(",")},1)`,
                                                            fontWeight: 'bold'
                                                        }}
                                                    >
                                                        {rule.code}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle
                                                        }}
                                                    >
                                                        {rule.attach}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle
                                                        }}
                                                    >
                                                        {counts[rule.code] || 0}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle,
                                                            width: '120px'
                                                        }}
                                                    >
                                                        <SelectableCell
                                                            value={editedComplexity[rule.id] !== undefined ? editedComplexity[rule.id] : (rule.complexity || '')}
                                                            onChange={(newComplexity) => handleComplexityChange(rule, newComplexity)}
                                                            darkMode={darkMode}
                                                            disabled={!['project_manager'].includes(user.roles[1])}
                                                            options={complexityOptions}
                                                        />
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle
                                                        }}
                                                    >
                                                        {rule.priority}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle,
                                                            textAlign: 'left'
                                                        }}
                                                    >
                                                        {rule.name}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle,
                                                            textAlign: 'left'
                                                        }}
                                                    >
                                                        {rule.description}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle
                                                        }}
                                                    >
                                                        {rule.foreshortening}
                                                    </TableCell>
                                                    <TableCell
                                                        sx={{
                                                            ...tableCellStyle,
                                                            width: '100px'
                                                        }}
                                                    >
                                                        <EditableCell
                                                            value={editedCosts[rule.id] !== undefined ? editedCosts[rule.id] : rule.cost}
                                                            onChange={(newCost) => handleCostChange(rule, newCost)}
                                                            darkMode={darkMode}
                                                            disabled={!['project_manager'].includes(user.roles[1])}
                                                        />
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )
                        })}
                    </Box>

                    {/* Action Buttons */}
                    <Box sx={{
                        zIndex: 9999,
                        position: 'fixed',
                        bottom: 30,
                        right: 40,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1
                    }}>
                        {/* Save Button */}
                        {(Object.keys(editedCosts).length > 0 || Object.keys(editedComplexity).length > 0) && (
                            <Fab
                                onClick={handleSaveChanges}
                                sx={{
                                    backgroundColor: '#4caf50',
                                    color: 'white',
                                    opacity: 0.8,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        opacity: 1,
                                        backgroundColor: '#4caf50'
                                    }
                                }}
                                size="small"
                            >
                                <SaveIcon />
                            </Fab>
                        )}

                        {/* Cancel Button */}
                        {(Object.keys(editedCosts).length > 0 || Object.keys(editedComplexity).length > 0) && (
                            <Fab
                                onClick={handleCancelChanges}
                                sx={{
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    opacity: 0.8,
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'scale(1.1)',
                                        opacity: 1,
                                        backgroundColor: '#f44336'
                                    }
                                }}
                                size="small"
                            >
                                <CancelIcon />
                            </Fab>
                        )}

                        {/* Scroll to Top Button */}
                        <Fab
                            size="small"
                            onClick={() => {
                                containerRef.current?.scrollTo(0, 0);
                            }}
                            sx={{
                                zIndex: 9999,
                                backgroundColor: '#66BB6A',
                                color: 'white',
                                opacity: 0.7,
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.2)',
                                    opacity: 1,
                                    backgroundColor: '#66BB6A'
                                }
                            }}
                        >
                            <ArrowUpwardIcon />
                        </Fab>
                        {/* Scroll to Top Button */}
                        {/* <Fab
                            onClick={() => containerRef.current.scrollTo(0, 0)}
                            sx={{
                                position: 'fixed',
                                bottom: 30,
                                right: 40,
                                display: printing ? 'none' : 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                width: 40,
                                height: 40,
                                backgroundColor: '#EC4137',
                                color: 'white',
                                border: 'none',
                                borderRadius: '50%',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
                                opacity: 0.5,
                                zIndex: 9999,
                                transition: 'color 0.3s ease, transform 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.2)',
                                    opacity: 1,
                                    backgroundColor: '#EC4137'
                                }
                            }}
                        >
                            <ArrowUpwardIcon />
                        </Fab> */}
                    </Box>

                    {/* Alert Snackbar */}
                    <Snackbar
                        open={snackbar.open}
                        autoHideDuration={6000}
                        onClose={() => setSnackbar({ ...snackbar, open: false })}
                        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                    >
                        <Alert
                            onClose={() => setSnackbar({ ...snackbar, open: false })}
                            severity={snackbar.severity}
                            sx={{ width: '100%' }}
                            variant="filled"
                        >
                            {snackbar.message}
                        </Alert>
                    </Snackbar>



                </Box>
                {localPrinting && (
                    <Box sx={{
                        position: 'absolute',
                        left: 0,
                        top: 0,
                        width: '100vw',
                        height: '100vh',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        zIndex: 12000,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        pointerEvents: 'auto'
                    }}>
                        <CircularProgress
                            size={60}
                            sx={{
                                color: '#66BB6A',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)'
                            }}
                        />
                    </Box>
                )}

            </ThemeProvider>,
            document.body
        )
    )

    // return (
    //     createPortal(
    //         <Box
    //             ref={containerRef}
    //             sx={{
    //                 position: 'absolute',
    //                 display: 'flex',
    //                 flexDirection: 'column',
    //                 top: 0,
    //                 zIndex: 1500,
    //                 backgroundColor: darkMode ? "#121212" : "#ffffff",
    //                 color: darkMode ? "#e0e0e0" : "#000000",
    //                 transition: "all 0.3s ease",
    //                 width: "100%",
    //                 height: "fit-content",
    //                 padding: "10px",
    //                 overflowY: 'auto',
    //             }}
    //         >
    //             <Box
    //                 sx={{
    //                     display: 'flex',
    //                     flexDirection: { xs: 'column', md: 'row' },
    //                     justifyContent: { xs: 'space-around', md: 'space-between' },
    //                     margin: '5px 0',
    //                     alignItems: 'center'
    //                 }}
    //             >
    //                 <Box
    //                     sx={{
    //                         display: 'flex',
    //                         flexDirection: 'row',
    //                         alignItems: 'center',
    //                         fontSize: { xs: '20px', md: '28px' }
    //                     }}
    //                 >
    //                     <TaskAlt
    //                         sx={{
    //                             color: darkMode ? 'white' : 'black',
    //                             fontSize: '32px',
    //                             marginRight: '8px'
    //                         }}
    //                     />
    //                     <ChecklistRtlIcon sx={{ fontSize: window.innerWidth < 768 ? 19 : 24, color: '#97ec37' }} />
    //                     <Typography
    //                         variant="h6"
    //                         component="span"
    //                         sx={{
    //                             fontWeight: 'bold',
    //                             fontSize: { xs: '20px', md: '28px' }
    //                         }}
    //                     >
    //                         Чек-лист контроля качества строительства
    //                     </Typography>
    //                 </Box>

    //                 <Box
    //                     sx={{
    //                         display: 'flex',
    //                         flexDirection: 'row',
    //                         justifyContent: 'end',
    //                         alignItems: 'center',
    //                         fontSize: { xs: '14px', md: '18px' }
    //                     }}
    //                 >
    //                     <TextField
    //                         placeholder="Поиск"
    //                         value={textSearch}
    //                         onChange={(e) => setTextSearch(e.target.value)}
    //                         size="small"
    //                         sx={{
    //                             width: "280px",
    //                             margin: '0 10px',
    //                             '& .MuiOutlinedInput-root': {
    //                                 backgroundColor: darkMode ? '#1e1e1e' : 'white',
    //                                 color: darkMode ? 'white' : 'black',
    //                                 '& fieldset': {
    //                                     borderColor: '#ccc',
    //                                 },
    //                                 '&:hover fieldset': {
    //                                     borderColor: '#999',
    //                                 },
    //                                 '&.Mui-focused fieldset': {
    //                                     borderColor: '#80cd5c',
    //                                 },
    //                             },
    //                             '& .MuiInputBase-input': {
    //                                 color: darkMode ? 'white' : 'black',
    //                                 padding: '8px 12px',
    //                             },
    //                             '& .MuiInputLabel-root': {
    //                                 color: darkMode ? 'white' : 'black',
    //                             }
    //                         }}
    //                         InputProps={{
    //                             endAdornment: textSearch && (
    //                                 <InputAdornment position="end">
    //                                     <IconButton
    //                                         onClick={() => setTextSearch('')}
    //                                         edge="end"
    //                                         size="small"
    //                                         sx={{
    //                                             color: '#999',
    //                                             '&:hover': {
    //                                                 color: darkMode ? 'white' : 'black',
    //                                             }
    //                                         }}
    //                                     >
    //                                         <Clear fontSize="small" />
    //                                     </IconButton>
    //                                 </InputAdornment>
    //                             )
    //                         }}
    //                     />

    //                     <IconButton
    //                         onClick={() => setDark(!darkMode)}
    //                         sx={{
    //                             ...toggleButtonStyle,
    //                             color: darkMode ? 'white' : 'black',
    //                             marginRight: '8px'
    //                         }}
    //                     >
    //                         {darkMode ? <LightMode /> : <DarkMode />}
    //                     </IconButton>

    //                     <IconButton
    //                         onClick={() => dispatch(setModal(null))}
    //                         sx={{
    //                             ...toggleButtonStyle,
    //                             color: darkMode ? 'white' : 'black'
    //                         }}
    //                     >
    //                         <Close />
    //                     </IconButton>
    //                 </Box>
    //             </Box>

    //             {rules && getSortedRules(rules).map((group, i) => (
    //                 <Paper
    //                     key={`rules-table-container_${i}`}
    //                     elevation={0}
    //                     sx={{
    //                         display: 'flex',
    //                         flexDirection: 'column',
    //                         marginBottom: '0px',
    //                         backgroundColor: 'transparent',
    //                         overflow: 'hidden'
    //                     }}
    //                 >
    //                     <Table
    //                         sx={{
    //                             marginTop: '5px',
    //                             borderCollapse: 'collapse',
    //                             width: '100%',
    //                             backgroundColor: darkMode ? '#1e1e1e' : 'white',
    //                             '& .MuiTableCell-root': {
    //                                 borderBottom: '1px solid #ddd',
    //                                 fontSize: { xs: '10px', md: '14px' }
    //                             }
    //                         }}
    //                     >
    //                         <TableHead>
    //                             <TableRow>
    //                                 <TableCell
    //                                     colSpan={6}
    //                                     sx={{
    //                                         ...tableHeaderStyle,
    //                                         borderTopLeftRadius: '6px',
    //                                         borderTopRightRadius: '6px',
    //                                         background: darkMode ? '#121212' : '#ffffff',
    //                                         color: darkMode ? 'white' : 'black',
    //                                         padding: 0,
    //                                         border: 'none'
    //                                     }}
    //                                 >
    //                                     <Box
    //                                         sx={{
    //                                             display: 'flex',
    //                                             justifyContent: 'left',
    //                                             borderTopLeftRadius: '6px',
    //                                             borderLeft: "8px solid #80cd5c",
    //                                             padding: '3px 10px',
    //                                             marginBottom: '0px',
    //                                             fontSize: { xs: '18px', md: '20px' }
    //                                         }}
    //                                     >
    //                                         <Typography variant="h6" component="span">
    //                                             {group.group}
    //                                         </Typography>
    //                                     </Box>
    //                                 </TableCell>
    //                             </TableRow>
    //                             <TableRow>
    //                                 <TableCell
    //                                     onClick={() => handleSort('code')}
    //                                     sx={{
    //                                         ...tableHeaderStyle,
    //                                         whiteSpace: 'nowrap',
    //                                         cursor: 'pointer',
    //                                         '&:hover': {
    //                                             opacity: 0.8
    //                                         }
    //                                     }}
    //                                 >
    //                                     Код <SortIndicator column="code" />
    //                                 </TableCell>
    //                                 <TableCell sx={tableHeaderStyle}>Вложения</TableCell>
    //                                 <TableCell sx={tableHeaderStyle}>Приоритет</TableCell>
    //                                 <TableCell sx={tableHeaderStyle}>Отклонение</TableCell>
    //                                 <TableCell sx={tableHeaderStyle}>Предписание</TableCell>
    //                                 <TableCell sx={tableHeaderStyle}>Фото инструкция</TableCell>
    //                                 <TableCell sx={tableHeaderStyle}>Стоимость {<EditIcon />}</TableCell>
    //                             </TableRow>
    //                         </TableHead>
    //                         <TableBody>
    //                             {group.rules.sort((a, b) => {
    //                                 return sortOrder === 'asc' ? a.num_code - b.num_code : b.num_code - a.num_code
    //                             }).map((rule, indx) => (
    //                                 <TableRow
    //                                     key={indx}
    //                                     sx={indx % 2 === 1 ? evenRowStyle : {}}
    //                                 >
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'center',
    //                                             color: darkMode ? 'white' : 'black'
    //                                         }}
    //                                     >
    //                                         {rule.code}
    //                                     </TableCell>
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'left',
    //                                             color: darkMode ? 'white' : 'black'
    //                                         }}
    //                                     >
    //                                         {rule.attach}
    //                                     </TableCell>
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'left',
    //                                             color: darkMode ? 'white' : 'black'
    //                                         }}
    //                                     >
    //                                         {rule.priority}
    //                                     </TableCell>
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'left',
    //                                             color: darkMode ? 'white' : 'black'
    //                                         }}
    //                                     >
    //                                         {rule.name}
    //                                     </TableCell>
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'left',
    //                                             color: darkMode ? 'white' : 'black'
    //                                         }}
    //                                     >
    //                                         {rule.description}
    //                                     </TableCell>
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'left',
    //                                             color: darkMode ? 'white' : 'black'
    //                                         }}
    //                                     >
    //                                         {rule.foreshortening}
    //                                     </TableCell>
    //                                     <TableCell
    //                                         sx={{
    //                                             ...tableCellStyle,
    //                                             textAlign: 'center',
    //                                             color: darkMode ? 'white' : 'black',
    //                                             width: '100px',
    //                                             padding: '0 4px'
    //                                         }}
    //                                     >
    //                                         <EditableCell
    //                                             value={editedCosts[rule.id] !== undefined ? editedCosts[rule.id] : rule.cost}
    //                                             onChange={(newCost) => handleCostChange(rule.id, newCost)}
    //                                             darkMode={darkMode}
    //                                         />
    //                                     </TableCell>
    //                                 </TableRow>
    //                             ))}
    //                         </TableBody>
    //                     </Table>
    //                 </Paper>
    //             ))}

    //             {Object.keys(editedCosts).length > 0 && (
    //                 <Paper
    //                     elevation={3}
    //                     sx={{
    //                         position: 'fixed',
    //                         bottom: 20,
    //                         left: '50%',
    //                         transform: 'translateX(-50%)',
    //                         padding: '10px 20px',
    //                         zIndex: 1600,
    //                         display: 'flex',
    //                         gap: 2,
    //                         backgroundColor: darkMode ? '#333' : '#fff'
    //                     }}
    //                 >
    //                     <Button
    //                         variant="contained"
    //                         color="primary"
    //                         startIcon={<Save />}
    //                         onClick={handleSaveChanges}
    //                     >
    //                         Сохранить
    //                     </Button>
    //                     <Button
    //                         variant="outlined"
    //                         color="secondary"
    //                         startIcon={<Cancel />}
    //                         onClick={handleCancelChanges}
    //                     >
    //                         Отмена
    //                     </Button>
    //                 </Paper>
    //             )}

    //             <Snackbar
    //                 open={snackbar.open}
    //                 autoHideDuration={6000}
    //                 onClose={() => setSnackbar({ ...snackbar, open: false })}
    //                 anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
    //             >
    //                 <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
    //                     {snackbar.message}
    //                 </Alert>
    //             </Snackbar>

    //             <Fab
    //                 onClick={() => {
    //                     containerRef.current.scrollTo(0, 0)
    //                 }}
    //                 sx={scrollButtonStyle}
    //             >
    //                 <KeyboardArrowUp sx={{ color: 'white' }} />
    //             </Fab>
    //         </Box>,
    //         document.body
    //     )
    // );
};

export default ModalRules;
